import ky, { KyResponse } from 'ky';
import { apiUrl, apiTimeout } from '@/config';
import { ApiErrorMessage } from '@/types/errors';
import { AuthTokens } from '@/api/mutations/auth';
import { getAccessToken, getIdToken, setAccessToken, setIdToken } from '@/lib/tokenManager';

let isRefreshing = false;
let failedQueue: { resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    }
    else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Callback function that will be called when new tokens are received
let onNewTokensCallback: ((tokens: AuthTokens | null) => void) | null = null;
export const setOnNewTokensCallback = (
  callback: typeof onNewTokensCallback,
) => {
  onNewTokensCallback = callback;
};

const api = ky.extend({
  prefixUrl: apiUrl,
  timeout: apiTimeout ?? 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const idToken = getIdToken();
        const accessToken = getAccessToken();
        if (idToken && accessToken) {
          request.headers.set('Authorization', `Bearer ${request.url.includes('/logout') ? accessToken : idToken}`);
        }

        return request;
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        if (response.status !== 401 && response.status !== 403) {
          return response;
        }

        // Clone the response in order to allow next handlers to also read it
        const responseClone = response.clone() as KyResponse;
        const errorResponse = await responseClone.json<{ message: string }>();
        const message = errorResponse.message;

        // The token is expired and no refresh is in progress
        if ((message === 'Authentication failed' || message === 'JWT expired') && !isRefreshing) {
          isRefreshing = true;

          try {
            const response = await ky
              .post(`${apiUrl}/auth/refresh-token`, {
                credentials: 'include',
              })
              .json<{ tokens: AuthTokens }>();
            const { accessToken, idToken } = response.tokens;
            // Update global accessToken and idToken
            setAccessToken(accessToken);
            setIdToken(idToken);
            if (onNewTokensCallback) {
              onNewTokensCallback(response.tokens);
            }
            processQueue(null, idToken);
            request.headers.set('Authorization', `Bearer ${idToken}`);
            return await ky(request);
          }
          catch (error) {
            console.error('Token refresh failed:', error);
            setAccessToken(null);
            setIdToken(null);
            if (onNewTokensCallback) {
              onNewTokensCallback(null);
            }
            processQueue(error, null);
            return new Response(null, { status: 401 });
          }
          finally {
            isRefreshing = false;
          }
        }

        // A refresh is already happening so pause and queue this request.
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (token && typeof token === 'string') {
                request.headers.set('Authorization', `Bearer ${token}`);
              }
              return ky(request);
            })
            .catch((error: unknown) => {
              if (error instanceof Error) {
                return Promise.reject(error);
              }

              return Promise.reject(new Error('Unknown error occurred during token refresh'));
            });
        }

        // It's 401 or 403 for another reason
        return response;
      },
    ],
    beforeError: [
      async (error) => {
        const { response } = error;
        const apiError: { error?: ApiErrorMessage } = await response.json();

        error.message = apiError.error ?? error.message;
        return error;
      },
    ],
  },
});

export default api;
